import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clubService } from '@/services/club.service';
import type {
  AdminCreateClubRequest,
  SearchClubsRequest,
  UpdateClubModulesRequest,
  UpdateClubOperationalPreferencesRequest,
  UpdateClubRequest,
} from '@/types/club';
import type { ClubCapability } from '@/types/club-membership';
import type { MemberRole } from '@/types/club-member';

/**
 * Hook pour récupérer le club de l'utilisateur connecté
 */
export function useMyClub(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['club', 'me'],
    queryFn: async () => {
      const response = await clubService.getMyClub();
      return response.data;
    },
    // Seuls les comptes club ont une fiche : inutile d'interroger l'API — et de
    // récolter un 403 — depuis un profil joueur ou entraîneur.
    enabled: options?.enabled ?? true,
    // Un compte sans fiche club reçoit un 403 : réessayer ne changera rien tant
    // que l'administrateur n'a pas créé le profil.
    retry: (failureCount, error) => !isClubNotProvisioned(error) && failureCount < 1,
  });
}

/**
 * Le compte a-t-il cette capacité sur son club ?
 *
 * Sert à ne pas offrir un bouton dont chaque clic finirait en 403 : le serveur
 * reste seul juge, mais une action refusée ne doit pas être proposée.
 *
 * Tant que les capacités n'ont pas été chargées, la réponse est « non » :
 * afficher un bouton puis le retirer serait plus déroutant que l'inverse.
 */
export function useClubCapability(capability: ClubCapability): boolean {
  const { data: club } = useMyClub();
  return club?.capabilities?.includes(capability) ?? false;
}

/**
 * Le compte est bien connecté mais aucune fiche club ne lui est rattachée.
 * Distinguer ce cas d'une panne réseau permet d'afficher la bonne explication.
 */
export function isClubNotProvisioned(error: unknown): boolean {
  return (error as { response?: { status?: number } })?.response?.status === 403;
}

/**
 * Hook pour récupérer un club par son ID
 */
export function useClubById(clubId: string) {
  return useQuery({
    queryKey: ['club', clubId],
    queryFn: async () => {
      const response = await clubService.getClubById(clubId);
      return response.data;
    },
    enabled: !!clubId,
  });
}

/**
 * Hook pour mettre à jour le club de l'utilisateur connecté
 */
export function useUpdateMyClub() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateClubRequest) => clubService.updateMyClub(data),
    onSuccess: () => {
      // Invalider la requête du club pour forcer un refresh
      queryClient.invalidateQueries({ queryKey: ['club', 'me'] });
    },
  });
}

/**
 * Hook pour rechercher des clubs
 */
export function useSearchClubs(filters: SearchClubsRequest) {
  return useQuery({
    queryKey: ['clubs', 'search', filters],
    queryFn: async () => {
      const response = await clubService.searchClubs(filters);
      return {
        clubs: response.data,
        pagination: response.pagination,
      };
    },
  });
}

/**
 * Hook pour récupérer les préférences opérationnelles du club connecté
 */
export function useMyClubOperationalPreferences() {
  return useQuery({
    queryKey: ['club', 'me', 'preferences'],
    queryFn: async () => {
      const response = await clubService.getMyOperationalPreferences();
      return response.data;
    },
  });
}

/**
 * Hook pour mettre à jour les préférences opérationnelles du club connecté
 */
export function useUpdateMyClubOperationalPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateClubOperationalPreferencesRequest) =>
      clubService.updateMyOperationalPreferences(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['club', 'me', 'preferences'] });
      queryClient.invalidateQueries({ queryKey: ['club', 'me'] });
    },
  });
}

/**
 * Hook pour récupérer les modules activés du club connecté
 */
export function useMyClubModules() {
  return useQuery({
    queryKey: ['club', 'me', 'modules'],
    queryFn: async () => {
      const response = await clubService.getMyModules();
      return response.data;
    },
    retry: (failureCount, error) => !isClubNotProvisioned(error) && failureCount < 1,
  });
}

/**
 * Hook pour activer ou désactiver des modules du club connecté
 */
export function useUpdateMyClubModules() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateClubModulesRequest) => clubService.updateMyModules(data),
    onSuccess: () => {
      // La fiche club transporte aussi les modules : les deux caches doivent
      // repartir ensemble, sinon le menu resterait sur l'ancienne configuration.
      queryClient.invalidateQueries({ queryKey: ['club', 'me', 'modules'] });
      queryClient.invalidateQueries({ queryKey: ['club', 'me'] });
    },
  });
}

/**
 * Hook pour lister les clubs — administration de la plateforme
 */
export function useAdminClubs() {
  return useQuery({
    queryKey: ['admin', 'clubs'],
    queryFn: async () => {
      const response = await clubService.adminListClubs();
      return response.data;
    },
  });
}

/**
 * Hook pour créer un compte club — administration de la plateforme
 */
export function useAdminCreateClub() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AdminCreateClubRequest) => clubService.adminCreateClub(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'clubs'] });
    },
  });
}

/**
 * Hook pour bloquer un club — administration de la plateforme
 */
export function useAdminBlockClub() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ clubId, reason }: { clubId: string; reason?: string }) =>
      clubService.adminBlockClub(clubId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'clubs'] });
    },
  });
}

/**
 * Hook pour débloquer un club — administration de la plateforme
 */
export function useAdminUnblockClub() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (clubId: string) => clubService.adminUnblockClub(clubId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'clubs'] });
    },
  });
}

/**
 * Hook pour le décompte préalable à une suppression.
 *
 * Jamais mis en cache : un décompte périmé montrerait moins de données qu'il
 * n'en sera réellement détruit, et c'est sur ce chiffre que l'administrateur
 * décide.
 */
export function useAdminClubDeletionPreview(clubId: string | null) {
  return useQuery({
    queryKey: ['admin', 'clubs', clubId, 'deletion-preview'],
    queryFn: async () => {
      const response = await clubService.adminClubDeletionPreview(clubId!);
      return response.data;
    },
    enabled: Boolean(clubId),
    gcTime: 0,
    staleTime: 0,
  });
}

/**
 * Hook pour supprimer un club et ses données — administration de la plateforme
 */
export function useAdminDeleteClub() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ clubId, confirmName }: { clubId: string; confirmName: string }) =>
      clubService.adminDeleteClub(clubId, confirmName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'clubs'] });
    },
  });
}


/**
 * Hook pour la grille des droits par rôle du club.
 *
 * Réservée à qui a la main sur le club (`club:admin`) : inutile de la demander
 * depuis un compte qui n'y a pas droit.
 */
export function useMyRoleCapabilities(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['club', 'me', 'roles'],
    queryFn: async () => {
      const response = await clubService.getMyRoleCapabilities();
      return response.data;
    },
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook pour régler les droits d'un rôle.
 *
 * La réponse porte la grille entière : on la pose directement en cache plutôt
 * que de réinterroger l'API, le serveur venant de dire ce qui s'applique.
 */
export function useUpdateMyRoleCapabilities() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      role,
      capabilities,
    }: {
      role: MemberRole;
      capabilities: ClubCapability[] | null;
    }) => clubService.updateMyRoleCapabilities(role, capabilities),
    onSuccess: (response) => {
      queryClient.setQueryData(['club', 'me', 'roles'], response.data);
      // Les droits du compte connecté peuvent avoir changé avec le réglage.
      queryClient.invalidateQueries({ queryKey: ['club', 'me'] });
    },
  });
}
